import { ApiError } from "@/common/lib/api";
import {
  type AccessRequestStatus,
  useAdminAccessRequestDetail,
  useApproveAccessRequest,
  useRejectAccessRequest,
  useResendAccessRequest,
} from "@/features/admin-auth/api/adminAccessRequestsApi";
import { formatDateTime, relativeTime } from "@/features/admin-auth/lib/time";
import { StatusBadge } from "@/features/admin-auth/pages/AdminAccessRequestsPage";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

export function AdminAccessRequestDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";
  const navigate = useNavigate();
  const { data, isPending, error } = useAdminAccessRequestDetail(id);
  const approve = useApproveAccessRequest();
  const reject = useRejectAccessRequest();
  const resend = useResendAccessRequest();

  const [modal, setModal] = useState<"approve" | "reject" | "resend" | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState<string | null>(null);

  async function handleApprove() {
    try {
      await approve.mutateAsync(id);
      toast.success(`Acceso aprobado. Link enviado a ${data?.request.email}.`);
      setModal(null);
    } catch (err) {
      setModal(null);
      if (err instanceof ApiError) {
        if (err.code === "USER_ALREADY_ACTIVE") {
          toast.error("Ya existe un user activo con este email. No se puede aprobar.");
          return;
        }
        if (err.code === "INVALID_STATE") {
          toast.error("El estado cambió. Refrescá la página.");
          return;
        }
      }
      toast.error("Error al aprobar. Intentá de nuevo.");
    }
  }

  async function handleReject() {
    const trimmed = rejectReason.trim();
    if (!trimmed) {
      setRejectReasonError("La razón es obligatoria.");
      return;
    }
    if (trimmed.length > 500) {
      setRejectReasonError("Máximo 500 caracteres.");
      return;
    }
    try {
      await reject.mutateAsync({ id, reason: trimmed });
      toast.success("Solicitud rechazada.");
      setModal(null);
      setRejectReason("");
    } catch {
      setModal(null);
      toast.error("Error al rechazar. Intentá de nuevo.");
    }
  }

  async function handleResend() {
    try {
      const result = await resend.mutateAsync(id);
      toast.success("Nuevo link enviado.");
      setModal(null);
      navigate(`/admin/access-requests/${result.request.id}`, { replace: true });
    } catch {
      setModal(null);
      toast.error("Error al reenviar. Intentá de nuevo.");
    }
  }

  if (isPending) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-app-border border-t-app-emerald" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm">
        Error al cargar la solicitud.{" "}
        <Link to="/admin/access-requests" className="underline">
          Volver a la lista
        </Link>
      </div>
    );
  }

  const { request, emailAlreadyRegistered } = data;
  const isPendingStatus = request.status === "pending";
  const canResend = request.status === "approved" || request.status === "expired";

  return (
    <>
      <title>{request.email} · Admin HATO</title>

      <div className="flex flex-col gap-6">
        <Link
          to="/admin/access-requests"
          className="flex w-fit items-center gap-1.5 text-app-ink-3 text-sm transition-colors hover:text-app-ink-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Link>

        <div className="rounded-2xl border border-app-border bg-app-surface p-6">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <h1 className="break-all font-medium text-app-ink-1 text-xl tracking-tight">
              {request.email}
            </h1>
            <StatusBadge status={request.status as AccessRequestStatus} />
          </div>

          {emailAlreadyRegistered && (
            <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Este email ya está registrado como usuario activo. No se puede aprobar.</span>
            </div>
          )}

          <dl className="grid grid-cols-[auto,1fr] gap-x-8 gap-y-3 text-sm">
            <dt className="font-medium text-app-ink-3">Teléfono</dt>
            <dd className="font-mono text-app-ink-1">{request.phone}</dd>

            <dt className="font-medium text-app-ink-3">Creado</dt>
            <dd className="text-app-ink-1">
              {relativeTime(request.createdAt)} — {formatDateTime(request.createdAt)}
            </dd>

            <dt className="font-medium text-app-ink-3">Ya registrado</dt>
            <dd className="text-app-ink-1">{emailAlreadyRegistered ? "Sí" : "No"}</dd>

            {request.reviewedBy && (
              <>
                <dt className="font-medium text-app-ink-3">Revisado por</dt>
                <dd className="text-app-ink-1">{request.reviewedBy}</dd>

                <dt className="font-medium text-app-ink-3">Revisado</dt>
                <dd className="text-app-ink-1">
                  {request.reviewedAt ? formatDateTime(request.reviewedAt) : "—"}
                </dd>
              </>
            )}

            {request.rejectReason && (
              <>
                <dt className="font-medium text-app-ink-3">Razón de rechazo</dt>
                <dd className="text-app-ink-1">{request.rejectReason}</dd>
              </>
            )}

            {request.approvedLinkExpiresAt && (
              <>
                <dt className="font-medium text-app-ink-3">Link expira</dt>
                <dd className="text-app-ink-1">{formatDateTime(request.approvedLinkExpiresAt)}</dd>
              </>
            )}
          </dl>

          {isPendingStatus && (
            <div className="mt-6 flex flex-wrap gap-3 border-app-border border-t pt-6">
              <button
                type="button"
                onClick={() => setModal("approve")}
                disabled={emailAlreadyRegistered}
                className="rounded-xl bg-app-emerald px-5 py-2 font-medium text-sm text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Aprobar
              </button>
              <button
                type="button"
                onClick={() => setModal("reject")}
                className="rounded-xl border border-rose-200 bg-rose-50 px-5 py-2 font-medium text-rose-700 text-sm transition-colors hover:bg-rose-100"
              >
                Rechazar
              </button>
            </div>
          )}

          {canResend && (
            <div className="mt-6 border-app-border border-t pt-6">
              <button
                type="button"
                onClick={() => setModal("resend")}
                className="rounded-xl border border-app-border px-5 py-2 text-app-ink-2 text-sm transition-colors hover:bg-app-surface-2"
              >
                Resend magic link
              </button>
            </div>
          )}
        </div>
      </div>

      {modal === "approve" && (
        <Modal title="Confirmar aprobación" onClose={() => setModal(null)}>
          <p className="text-app-ink-2 text-sm">
            Se enviará un magic link a <strong>{request.email}</strong>.
          </p>
          {emailAlreadyRegistered && (
            <p className="mt-2 text-amber-700 text-sm">
              Advertencia: este email ya está registrado como usuario activo.
            </p>
          )}
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setModal(null)}
              className="rounded-xl border border-app-border px-4 py-2 text-app-ink-2 text-sm hover:bg-app-surface-2"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleApprove}
              disabled={approve.isPending}
              className="rounded-xl bg-app-emerald px-4 py-2 font-medium text-sm text-white hover:opacity-90 disabled:opacity-60"
            >
              {approve.isPending ? "Aprobando…" : "Aprobar"}
            </button>
          </div>
        </Modal>
      )}

      {modal === "reject" && (
        <Modal title="Rechazar solicitud" onClose={() => setModal(null)}>
          <label
            htmlFor="reject-reason"
            className="mb-1.5 block font-medium text-app-ink-2 text-sm"
          >
            Razón interna <span className="text-rose-500">*</span>
          </label>
          <textarea
            id="reject-reason"
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.target.value);
              if (rejectReasonError) setRejectReasonError(null);
            }}
            rows={4}
            placeholder="Describí por qué se rechaza esta solicitud…"
            className="w-full resize-none rounded-xl border border-app-border bg-app-bg px-4 py-3 text-app-ink-1 text-sm placeholder:text-app-ink-3 focus:border-app-emerald focus:outline-none focus:ring-1 focus:ring-app-emerald/30"
          />
          {rejectReasonError && (
            <p role="alert" className="mt-1 text-rose-600 text-xs">
              {rejectReasonError}
            </p>
          )}
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setModal(null)}
              className="rounded-xl border border-app-border px-4 py-2 text-app-ink-2 text-sm hover:bg-app-surface-2"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleReject}
              disabled={reject.isPending}
              className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 font-medium text-rose-700 text-sm hover:bg-rose-100 disabled:opacity-60"
            >
              {reject.isPending ? "Rechazando…" : "Rechazar"}
            </button>
          </div>
        </Modal>
      )}

      {modal === "resend" && (
        <Modal title="Reenviar magic link" onClose={() => setModal(null)}>
          <p className="text-app-ink-2 text-sm">
            Esto va a generar un nuevo link y enviar un email a <strong>{request.email}</strong>.
            ¿Confirmar?
          </p>
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setModal(null)}
              className="rounded-xl border border-app-border px-4 py-2 text-app-ink-2 text-sm hover:bg-app-surface-2"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={resend.isPending}
              className="rounded-xl bg-app-emerald px-4 py-2 font-medium text-sm text-white hover:opacity-90 disabled:opacity-60"
            >
              {resend.isPending ? "Enviando…" : "Reenviar"}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="presentation"
    >
      <div className="w-full max-w-md rounded-2xl bg-app-surface p-6 shadow-lg">
        <h2 className="mb-4 font-medium text-app-ink-1 text-lg tracking-tight">{title}</h2>
        {children}
      </div>
    </div>
  );
}
